import { useEffect, useState } from 'react';
import { OziraMark } from 'ozira-shell-kit';
import { resolveShare, type ResolvedShare, type ResolvedShareMessage } from '../lib/conversationsApi';
import './sharedConversation.css';

function MessageBubble({ message }: { message: ResolvedShareMessage }) {
  return (
    <div className={`ozira-shared-row ozira-shared-row--${message.role}`}>
      <div className="ozira-shared-bubble">
        {message.image ? <img className="ozira-shared-bubble__image" src={message.image} alt="Shared attachment" /> : null}
        <p>{message.content}</p>
      </div>
    </div>
  );
}

// Public, UNAUTHENTICATED, read-only view of a share link (GET
// /api/shares/resolve?token=.. -- lib/shareGrants.js's resolveShare()). No
// Composer, no sign-in required: the bearer token IS the authority.
export function SharedConversationScreen({ token }: { token: string }) {
  const [state, setState] = useState<{ status: 'loading' } | { status: 'ready'; share: ResolvedShare } | { status: 'error' }>({ status: 'loading' });

  useEffect(() => {
    let active = true;
    setState({ status: 'loading' });
    resolveShare(token)
      .then((share) => { if (active) setState({ status: 'ready', share }); })
      .catch(() => { if (active) setState({ status: 'error' }); });
    return () => { active = false; };
  }, [token]);

  if (state.status === 'loading') {
    return (
      <main className="ozira-shared-screen" aria-label="Shared conversation">
        <p role="status">Loading shared content…</p>
      </main>
    );
  }

  if (state.status === 'error') {
    return (
      <main className="ozira-shared-screen" aria-label="Shared conversation">
        <p role="alert">This link is invalid or has expired.</p>
      </main>
    );
  }

  const { share } = state;
  const messages = share.resourceType === 'response' ? [share.message] : share.messages;

  return (
    <div className="ozira-shared-page">
      <header className="ozira-shared-topbar">
        <OziraMark size={24} />
        <span className="ozira-shared-topbar__brand">OZIRA</span>
        <span className="ozira-shared-topbar__divider" aria-hidden="true" />
        <span className="ozira-shared-topbar__title">{share.title}</span>
        <span className="ozira-shared-topbar__badge">Shared · view only</span>
        <div className="ozira-shared-topbar__spacer" />
        <a className="ozira-shared-topbar__cta" href="/">Continue in OZIRA</a>
      </header>
      <main className="ozira-shared-screen" aria-label="Shared conversation">
        <div className="ozira-shared-messages">
          {messages.map((message, index) => <MessageBubble key={index} message={message} />)}
        </div>
        {share.resourceType === 'response' && share.sources?.length ? (
          <div className="ozira-shared-sources" aria-label="Sources">
            {share.sources.map((source) => (
              <span key={`${source.label}-${source.href ?? ''}`}>{source.href ? <a href={source.href} target="_blank" rel="noreferrer">{source.label}</a> : source.label}</span>
            ))}
          </div>
        ) : null}
      </main>
      <footer className="ozira-shared-footer">
        This is a shared, read-only copy. Sign in to OZIRA to ask your own questions.
      </footer>
    </div>
  );
}
